/**
 * Writing a comment — and choosing, in the open, who will be able to read it.
 *
 * §8.1 gives the `internal` flag a default (internal) and a consequence
 * (a client reading a margin note that was never meant for them). A default with
 * a consequence that severe cannot live behind a menu: the audience control is
 * the second thing in this form, above the fold, phrased as two sentences rather
 * than as a checkbox called "internal". `audience.ts` holds the wording and
 * argues for it.
 *
 * §8.2's suggestion is folded into the same form rather than given a second
 * one, because it *is* a comment — "a comment carrying proposed replacement
 * text" — and a separate composer would imply a separate object with its own
 * lifecycle, which is exactly the design §8.2 rules out.
 */

import { type FormEvent, type ReactElement, useState } from 'react';
import { Form, Label, Radio, RadioGroup, ToggleButton } from 'react-aria-components';
import { Button } from '../../components/ui/button.tsx';
import { cn } from '../../components/ui/cn.ts';
import { ErrorState } from '../../components/ui/feedback.tsx';
import { TextField } from '../../components/ui/field.tsx';
import type { BlockAnchor } from './anchor.ts';
import {
  type AudienceContext,
  type AudienceValue,
  audienceChoices,
  audienceNotice,
  DEFAULT_AUDIENCE,
} from './audience.ts';
import { useCreateComment } from './queries.ts';
import { quoted } from './text.ts';

export interface CommentComposerProps {
  nodeId: string;
  anchor: BlockAnchor;
  /**
   * What the block says, for a block-level anchor — which quotes nothing, so
   * "this block" is all the anchor can say and not enough to write against.
   */
  blockPreview: string;
  audience: AudienceContext;
  /** Edits Yjs has not shipped yet — the API validates anchors against the stored draft. */
  pendingSync: boolean;
  onCancel: () => void;
  onCreated: (commentId: string) => void;
}

export function CommentComposer({
  nodeId,
  anchor,
  blockPreview,
  audience,
  pendingSync,
  onCancel,
  onCreated,
}: CommentComposerProps): ReactElement {
  const create = useCreateComment(nodeId);
  const [body, setBody] = useState('');
  const [choice, setChoice] = useState<AudienceValue>(DEFAULT_AUDIENCE);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(anchor.text);

  const choices = audienceChoices(audience);
  // A block-level anchor covers no text, so there is nothing for a replacement
  // to replace; §8.2's one click needs a range to put words into.
  const canSuggest = anchor.text.length > 0;
  const unchanged = suggesting && suggestion === anchor.text;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (body.trim() === '' || unchanged) return;
    create.mutate(
      {
        body: body.trim(),
        anchor,
        // Sent only when this person has a choice to make. A client's comment is
        // forced external by the database whatever is sent, and sending a value
        // that will be overruled is how a UI ends up believing its own request.
        ...(audience.seesInternal ? { internal: choice === 'internal' } : {}),
        ...(suggesting ? { suggestedText: suggestion } : {}),
      },
      { onSuccess: (data) => onCreated(data.comment.id) },
    );
  };

  return (
    <Form
      onSubmit={submit}
      className="flex flex-col gap-3 rounded-lg border border-brand bg-secondary p-4"
    >
      <p className="text-xs text-tertiary">
        {anchor.text.length === 0 ? (
          <>
            Commenting on the whole block
            {blockPreview.trim() === '' ? null : (
              <>
                {' — '}
                <span className="text-secondary italic">“{quoted(blockPreview)}”</span>
              </>
            )}
            . It stays anchored for as long as the block does.
          </>
        ) : (
          <>
            Commenting on <span className="text-secondary italic">“{quoted(anchor.text)}”</span>
          </>
        )}
      </p>

      <TextField
        label="Comment"
        multiline
        rows={3}
        value={body}
        onChange={setBody}
        autoFocus
        isRequired
      />

      {audience.seesInternal ? (
        <RadioGroup
          value={choice}
          onChange={(value) => setChoice(value as AudienceValue)}
          className="flex flex-col gap-1.5"
        >
          <Label className="block text-sm font-medium text-secondary">Who can read this</Label>
          <div className="grid gap-2 sm:grid-cols-2">
            {choices.map((option) => (
              <Radio
                key={option.value}
                value={option.value}
                className={cn(
                  'cursor-pointer rounded-lg border border-primary bg-primary p-3 text-sm',
                  'data-hovered:border-brand',
                  'data-selected:border-brand data-selected:bg-brand-primary',
                  'data-focus-visible:outline-2 data-focus-visible:outline-offset-2',
                  'data-focus-visible:outline-brand-500',
                )}
              >
                <span className="block font-medium text-primary">{option.label}</span>
                <span className="mt-0.5 block text-xs text-tertiary">{option.detail}</span>
              </Radio>
            ))}
          </div>
        </RadioGroup>
      ) : (
        <p className="text-xs text-tertiary">{audienceNotice(audience)}</p>
      )}

      {canSuggest ? (
        <div className="flex flex-col gap-2">
          <ToggleButton
            isSelected={suggesting}
            onChange={setSuggesting}
            className={cn(
              'w-fit cursor-pointer rounded-lg border border-primary px-3 py-1.5 text-xs font-semibold',
              'text-secondary data-hovered:bg-primary-hover',
              'data-selected:border-brand data-selected:bg-brand-primary data-selected:text-brand-secondary',
            )}
          >
            {suggesting ? 'Suggesting a replacement' : 'Suggest a replacement'}
          </ToggleButton>

          {suggesting ? (
            <TextField
              label="Replace the quoted text with"
              multiline
              rows={2}
              value={suggestion}
              onChange={setSuggestion}
              description="An editor can apply this in one click. Leave it empty to propose deleting the text."
              {...(unchanged
                ? { errorMessage: 'That suggestion proposes the text that is already there.' }
                : {})}
            />
          ) : null}
        </div>
      ) : null}

      {pendingSync ? (
        <p className="text-xs text-warning-primary">
          Your last edits are still syncing. Comments are anchored against the saved draft, so one
          on text you just typed may be refused for a moment.
        </p>
      ) : null}

      {create.isError ? <ErrorState error={create.error} /> : null}

      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="tertiary" onPress={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          variant="primary"
          isPending={create.isPending}
          isDisabled={body.trim() === '' || unchanged}
        >
          {/* The audience is named on the button as well as in the control
              above it. The radio group is what someone reads while composing;
              this is what they read at the moment they commit, which is the
              moment the choice stops being reversible. */}
          {audience.seesInternal
            ? choice === 'internal'
              ? 'Comment internally'
              : 'Comment where the client can read it'
            : 'Comment'}
        </Button>
      </div>
    </Form>
  );
}

/**
 * The reply box — one field, and no audience control.
 *
 * A reply inherits its thread's `internal` flag; the API refuses the field
 * rather than discarding it, on the grounds that a silently dropped `internal`
 * is a disclosure bug waiting for someone to trust the request they sent. So the
 * form does not offer what it cannot send, and says which audience it is
 * joining instead.
 */
export function ReplyComposer({
  nodeId,
  parentCommentId,
  internal,
  onDone,
}: {
  nodeId: string;
  parentCommentId: string;
  internal: boolean;
  onDone: () => void;
}): ReactElement {
  const create = useCreateComment(nodeId);
  const [body, setBody] = useState('');

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        if (body.trim() === '') return;
        create.mutate(
          { body: body.trim(), parentCommentId },
          {
            onSuccess: () => {
              setBody('');
              onDone();
            },
          },
        );
      }}
      className="flex flex-col gap-2"
    >
      <TextField label="Reply" multiline rows={2} value={body} onChange={setBody} autoFocus />
      <p className="text-xs text-tertiary">
        {internal
          ? 'Replies join the thread, so this is internal too.'
          : 'This thread is readable by everyone who can read the document, so this reply is too.'}
      </p>
      {create.isError ? <ErrorState error={create.error} /> : null}
      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="tertiary" onPress={onDone}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          variant="secondary"
          isPending={create.isPending}
          isDisabled={body.trim() === ''}
        >
          Reply
        </Button>
      </div>
    </Form>
  );
}
